import React, { useState, useEffect, useRef } from "react"
import toast from "react-hot-toast"
import { get, post, remove } from "./../utils/apiClient"
import { saveAs } from "file-saver"
import { Loader } from "./../utils/common"

const AddressBook = ({ authUser }) => {
  const [rootRef, setRootRef] = useState("") // Root folder identifier
  const [folders, setFolders] = useState([]) // List of subfolders
  //const [contents, setContents] = useState([]) // List of subfolders
  const [isAdding, setIsAdding] = useState(false)
  const [inputValue, setInputValue] = useState("") // New folder name
  const [deleteTarget, setDeleteTarget] = useState(null) // Folder name to delete
  const [uploadTarget, setUploadTarget] = useState(null) // Folder name to upload files
  // const [uploadModal, setUploadModal] = useState(null)
  const [fileList, setFileList] = useState([]) // store File[]

  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef(null)
  const fileInputRefDirectUpload = useRef(null)

  // Fetch folder structure on mount or after mutations
  useEffect(() => {
    loadFolderData()
  }, [])

  const loadFolderData = async () => {
    setLoading(true)
    await fetchFolderData()
    setLoading(false)
  }

  const fetchFolderData = async () => {
    try {
      const res = await get("/folderData", { ref: authUser?.faxNumber })
      setRootRef(res.folderName)
      setFolders(res.subfolders || [])
      setFileList(res?.contents || [])
    } catch (error) {
      setRootRef("")
      setFolders([])
      setFileList([])
      toast.error(error)
    }
  }

  // Create new subfolder under root
  const handleSave = async () => {
    const name = inputValue.trim()
    if (!name) {
      toast.error("Please enter folder name")
      return
    }

    setLoading(true)
    try {
      const url = `/createSubFolder?ref=${encodeURIComponent(
        rootRef,
      )}&subfolder=${encodeURIComponent(name)}`
      const res = await post(url)
      if (!res.ok) throw new Error()
      toast.success("Folder created")
      setInputValue("")
      setIsAdding(false)
      fetchFolderData()
      setLoading(false)
    } catch (error) {
      toast.error(error)
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setInputValue("")
    setIsAdding(false)
  }

  // Prompt delete confirmation
  const confirmDelete = (folderName) => {
    setDeleteTarget(folderName)
  }

  // Delete folder via API
  const handleDelete = async () => {
    setLoading(true)

    try {
      const url = `/deleteSubFolder?ref=${encodeURIComponent(
        rootRef,
      )}&subfolder=${encodeURIComponent(deleteTarget)}`
      const res = await remove(url, {}, { ref: authUser?.faxNumber })
      if (!res.ok) throw new Error()
      toast.success("Folder deleted")
      setDeleteTarget(null)
      fetchFolderData()
      setLoading(false)
    } catch (error) {
      toast.error(error)
      setLoading(false)
    }
  }

  // Trigger hidden file input for upload
  const triggerFilePicker = (folderName) => {
    setUploadTarget(folderName)
    fileInputRef.current.click()
  }

  const triggerFilePickerForDirectUpload = () => {
    setUploadTarget("")
    fileInputRefDirectUpload.current.click()
  }

  // Upload selected files on change
  const onFileChange = async (e, isDirectUpload = false) => {
    setLoading(true)
    const files = Array.from(e.target.files)

    if (isDirectUpload) {
      if (files.length === 0) {
        setLoading(false)
        return
      }
    } else {
      if (!uploadTarget || files.length === 0) {
        setLoading(false)
        return
      }
    }

    try {
      await Promise.all(
        files.map((file) => {
          const formData = new FormData()
          formData.append("files", file)
          const headers = {
            ref: rootRef,
            subfolder: uploadTarget,
            filename: file.name,
          }
          return post("/uploadFile", formData, headers)
        }),
      )

      if (files.length === 1) {
        toast.success("File uploaded")
      } else {
        toast.success("Files uploaded")
      }

      fetchFolderData()
      setLoading(false)
    } catch (error) {
      toast.error(error)
      setLoading(false)
    } finally {
      e.target.value = null
      setUploadTarget(null)
      setLoading(false)
    }
  }

  const handleDownloadFile = async (subfolder, filename) => {
    setLoading(true)
    try {
      const headers = {
        ref: rootRef,
        subfolder,
        filename,
      }
      const res = await get("/downloadFile", headers)

      const blob = await res.blob()

      saveAs(blob, filename)

      if (!res.ok) throw new Error()
      toast.success("File downloaded")
      setLoading(false)
    } catch (error) {
      toast.error(error)
      setLoading(false)
    }
  }

  // Delete a file via API
  const handleDeleteFile = async (subfolder, filename) => {
    setLoading(true)
    try {
      const headers = {
        ref: rootRef,
        subfolder,
        filename,
      }
      const res = await remove("/deleteFile", {}, headers)
      if (!res.ok) throw new Error()
      toast.success("File deleted")
      fetchFolderData()
      setLoading(false)
    } catch (error) {
      toast.error(error)
      setLoading(false)
    }
  }

  const handleSendBulkFax = async (folderName, isDirectUpload = false) => {
    let files = null
    let requiredFile = null
    if (isDirectUpload) {
      files = fileList.map((f) => f)
    } else {
      const folder = folders.find((f) => f.folderName === folderName)
      if (!folder) return
      files = folder.contents || []
    }

    requiredFile = files.find((file) =>
      ["sendFaxNumbers.csv", "sendFaxNumbers.txt"].includes(file),
    )

    if (!files.length) {
      toast.error("No files selected")
      return
    }

    // const maxSize = 95 * 1024 * 1024
    // const totalSize = files.reduce((acc, file) => acc + file.file.size, 0)
    // console.log("-> totalSize:", totalSize)
    // console.log("-> maxSize:", maxSize)

    // if (totalSize > maxSize) {
    //   toast.error("Total file size must be less than 95MB", {
    //     duration: 5000,
    //     position: "top-right",
    //   })
    //   return
    // }

    if (!requiredFile) {
      toast.error("You must include sendFaxNumbers.csv or sendFaxNumbers.txt", {
        duration: 5000,
      })
      return
    }

    // const text = await readFileText(requiredFile.file) // Use the file object from filesMeta
    // const lines = text
    //   .split(/\r?\n/)
    //   .map((line) => line.trim())
    //   .filter((line) => line !== "")

    // const allValidLines = lines.every((line) => validateFaxNumber(line.trim()))

    // if (!allValidLines || lines.length === 0) {
    //   toast.error(
    //     "Each line in sendFaxNumbers.csv or sendFaxNumbers.txt must contain a valid fax number",
    //     {
    //       duration: 5000,
    //       position: "top-right",
    //     }
    //   )
    //   return
    // }

    // ✅ All validations passed
    setLoading(true)
    try {
      const url = `/sendFax?ref=${encodeURIComponent(
        rootRef,
      )}&subfolder=${encodeURIComponent(folderName)}`
      await post(url)

      toast.success("Bulk fax sent successfully!")
      setLoading(false)
      //setUploadModal(false)
      setFileList([])
      fetchFolderData()
    } catch (error) {
      toast.error(error)
      setLoading(false)
    }
  }

  return (
    <div className={`document-list`}>
      {/* Create Folder Section */}
      <div className={`address-book-container`}>
        {loading && (
          <div className="loading-overlay">
            <Loader />
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            // height: "80vh",
          }}
        >
          <div className="send-bulk-fax-block">
            <div style={{ padding: "10px" }}>
              <span style={{ fontSize: "22px" }}>Send Bulk Fax</span>
              <div
                className="note"
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    marginBottom: "10px",
                  }}
                >
                  Upload files and send bulk fax without creating a folder
                </span>
                <span
                  style={{
                    fontSize: "12px",
                  }}
                >
                  Steps: Click Upload ➝ Select files ➝ Send bulk fax
                </span>
                <span
                  style={{
                    fontSize: "12px",
                  }}
                >
                  Note: Select file(s) you intend to fax, include a file named
                  "sendFaxNumbers.txt" OR "sendFaxNumbers.csv" with each fax
                  number on a new line.
                </span>
              </div>

              {fileList?.length === 0 && (
                <div className="address-book-action">
                  <button
                    className="add-btn"
                    style={{ fontSize: "12px" }}
                    onClick={() => triggerFilePickerForDirectUpload()}
                  >
                    Upload 📤
                  </button>
                </div>
              )}
            </div>

            {fileList?.length > 0 && (
              <div>
                <ul style={{ padding: "10px" }}>
                  <li className="document-list-folder-li">
                    <div className="folder-header">
                      <strong className="category">
                        <span className="icon">📁</span> Root
                      </strong>
                      <div className="folder-actions">
                        <button
                          title="Add More Files"
                          // onClick={() => triggerFilePicker("")}
                          onClick={() => triggerFilePickerForDirectUpload()}
                        >
                          ➕
                        </button>
                        {/* <button
                          title="Delete Folder"
                          onClick={() => confirmDelete("")}
                        >
                          🗑️
                        </button> */}

                        {fileList.length > 0 && (
                          <span
                            className="bottom-right-btn expandable-btn"
                            //onClick={() => handleSendBulkFax("")}
                            onClick={() => handleSendBulkFax("", true)}
                          >
                            ➡️ <span>Send Bulk Fax</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="documentName">
                      <ul>
                        {fileList.map((file) => (
                          <li className="file-item" key={file}>
                            <span className="user-icon">📄</span>
                            <span className="file-name">{file}</span>
                            <span className="file-actions">
                              {/* <button
                                title="Download"
                                onClick={() => handleDownloadFile("", file)}
                              >
                                ⬇️
                              </button> */}
                              <button
                                title="Delete"
                                onClick={() => handleDeleteFile("", file)}
                              >
                                🗑️
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                </ul>

                {/* <div style={{ paddingTop: "10px", marginLeft: "10px" }}>
                  Root files
                  <ul
                    style={{
                      paddingTop: "5px",
                      overflowY: "auto",
                      maxHeight: "200px",
                    }}
                  >
                    {fileList.map((file, idx) => (
                      <li
                        key={idx}
                        style={{ padding: "5px", backgroundColor: "unset" }}
                      >
                        <span style={{ marginRight: "5px" }}>📄</span>
                        <span style={{ fontWeight: "normal" }}>
                          {file}
                        </span>{" "}
                        <span
                          style={{ fontSize: "12px", cursor: "pointer" }}
                          onClick={() => handleDeleteFile("", file)}
                        >
                          🗑️
                        </span>
                      </li>
                    ))}
                  </ul>
                </div> */}
              </div>
            )}
          </div>

          <div className="address-book-block">
            <div style={{ padding: "10px" }}>
              <span style={{ fontSize: "22px" }}> Address Book</span>
              <div className="note">
                Create folder first, upload files and then send bulk fax
              </div>

              <div className="address-book-action">
                {isAdding ? (
                  <div className="input-wrapper fade-in">
                    <input
                      type="text"
                      value={inputValue}
                      // onChange={(e) => setInputValue(e.target.value)}
                      onChange={(e) => {
                        if (e.target.value.includes("|")) return
                        setInputValue(e.target.value)
                      }}
                      className="input-box"
                      placeholder="Folder name"
                      maxLength={40}
                    />
                    <button className="save-btn" onClick={handleSave}>
                      Create
                    </button>
                    <button className="cancel-btn" onClick={handleCancel}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      className="add-btn"
                      onClick={() => setIsAdding(true)}
                      style={{ marginLeft: "0px", fontSize: "12px" }}
                    >
                      Create Folder 📁
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Folder List */}
            <div
              style={{
                // overflowY: "auto",
                // maxHeight: "60vh",
                paddingLeft: "10px",
                // height: "calc(100vh - 280px)",
                marginRight: "10px",
              }}
            >
              <ul>
                {folders.map((folder) => (
                  <li
                    className="document-list-folder-li"
                    key={folder.folderName}
                  >
                    <div className="folder-header">
                      <strong className="category">
                        <span className="icon">📁</span> {folder.folderName}
                      </strong>
                      <div className="folder-actions">
                        <button
                          title="Add More Files"
                          onClick={() => triggerFilePicker(folder.folderName)}
                        >
                          ➕
                        </button>
                        <button
                          title="Delete Folder"
                          onClick={() => confirmDelete(folder.folderName)}
                        >
                          🗑️
                        </button>

                        {folder.contents.length > 0 && (
                          <span
                            className="bottom-right-btn expandable-btn"
                            onClick={() => handleSendBulkFax(folder.folderName)}
                          >
                            ➡️ <span>Send Bulk Fax</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="documentName">
                      <ul>
                        {folder.contents.map((file) => (
                          <li className="file-item" key={file}>
                            <span className="user-icon">📄</span>
                            <span className="file-name">{file}</span>
                            <span className="file-actions">
                              <button
                                title="Download"
                                onClick={() =>
                                  handleDownloadFile(folder.folderName, file)
                                }
                              >
                                ⬇️
                              </button>
                              <button
                                title="Delete"
                                onClick={() =>
                                  handleDeleteFile(folder.folderName, file)
                                }
                              >
                                🗑️
                              </button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={(e) => onFileChange(e)}
      />

      <input
        type="file"
        multiple
        ref={fileInputRefDirectUpload}
        style={{ display: "none" }}
        onChange={(e) => onFileChange(e, true)}
      />

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="modal-overlay">
          <div className="modal">
            <p>Are you sure you want to delete folder '{deleteTarget}'?</p>
            <div className="modal-actions">
              <button onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* {uploadModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ minWidth: "500px" }}>
            {fileList.length ? (
              <ul>
                {fileList.map((file, idx) => (
                  <li key={idx} style={{ borderBottom: "1px dashed #ccc" }}>
                    <span style={{ marginRight: "5px" }}>📄</span>
                    <span>{file}</span>
                    
                  </li>
                ))}
              </ul>
            ) : (
              <p>No files selected.</p>
            )}

            <div className="modal-actions">
              <span>
                <button
                  onClick={() => {
                    setUploadModal(false)
                  }}
                >
                  Cancel
                </button>
              </span>

              <span>
                <button onClick={() => triggerFilePickerForDirectUpload()}>
                  Add More Files
                </button>

                <button
                  onClick={() => handleSendBulkFax("", true)}
                  disabled={!fileList.length}
                  style={{ marginLeft: "5px" }}
                >
                  Send Bulk Fax
                </button>
              </span>
            </div>
          </div>
        </div>
      )} */}
    </div>
  )
}

export default AddressBook
